// Le regole pure dei WhatsApp (piano M6, R1; D2, D4, D5, D11, D12, D25): i modelli di partenza e le loro variabili, il link
// `wa.me` del modo a mano, la riga «scrivi STOP» e le richieste di STOP, la scelta del canale per un messaggio, la finestra di
// 24 ore e il tetto per destinatario. Niente database e niente fornitori: `manual`, `meta` e `agile` sono identificativi di
// prodotto, i tre modi di mandare un WhatsApp; ciò che è dei fornitori sta negli adattatori del server. Le etichette italiane
// sono dei cataloghi. Il file importa soltanto i tipi degli esiti e la pulizia dei parametri della porta del WhatsApp ufficiale
// (`whatsapp-provider.ts`, senza altre dipendenze), così l'interfaccia lo prende dal sottopercorso `@gioia365/domain/whatsapp` senza
// il dataset territoriale.
import { cleanTemplateParameter } from "./whatsapp-provider.js";
/** I tre WhatsApp (D2): `manual` è il modo a mano, sempre acceso; `meta` il WhatsApp ufficiale; `agile` WhatsApp Agile. */
export const WHATSAPP_CHANNELS = ["manual", "meta", "agile"];
/** I sei modelli di partenza (D5), nell'ordine del mock-up. */
export const WHATSAPP_TEMPLATE_KEYS = [
    "first_contact",
    "after_no_answer",
    "after_answer",
    "callback_confirmation",
    "disco_confirmation",
    "demo_confirmation",
];
/** La categoria di Meta di un modello: le conferme sono di utilità, il resto è marketing. */
export const WHATSAPP_TEMPLATE_CATEGORIES = ["utility", "marketing"];
/**
 * Che cosa ha mandato il Lead in un messaggio in arrivo (piano M6, R3, passo 11a; questione 425): un testo, oppure un'immagine, un
 * audio, un video, un documento, uno sticker, una posizione o un contatto, che Gioia365 non scarica (la Timeline e «Conversazioni»
 * dicono di aprirli sul telefono); `other` è un messaggio di un tipo che Gioia365 non conosce, che si dice allo stesso modo. Le
 * reazioni, i messaggi di sistema e quelli che il fornitore stesso non sa leggere non sono messaggi del Lead: non si salvano.
 */
export const WHATSAPP_CONTENT_KINDS = ["text", "image", "audio", "video", "document", "sticker", "location", "contacts", "other"];
/**
 * Le cinque variabili dei modelli, scritte fra graffe nel testo (D5, WA-13): `{nome}`, `{AziendaTarget}` (l'azienda del Lead, il suo
 * «Nome Azienda Target»), `{commerciale}`, `{NostraAzienda}` (l'azienda che usa Gioia365), `{giorno e ora}`. Sono le sole che la
 * pagina offre.
 */
export const TEMPLATE_VARIABLES = ["nome", "AziendaTarget", "commerciale", "NostraAzienda", "giorno e ora"];
/**
 * I nomi di prima (fino al 16/10/2026; WA-13, decisione di Lucio del 17/10/2026), che valgono per sempre come i nuovi: un testo salvato
 * con `{salone}` o `{azienda}` si riempie, si conta e va a Meta come con `{AziendaTarget}` e `{NostraAzienda}`. Non si offrono e non si
 * dicono mai: chi legge il testo vede i nomi nuovi (`canonicalTemplateBody`), e una variabile che manca si dice con il nome nuovo.
 */
export const TEMPLATE_VARIABLE_ALIASES = Object.freeze({
    salone: "AziendaTarget",
    azienda: "NostraAzienda",
});
const template = (value) => Object.freeze(value);
/**
 * I sei modelli con i nomi, i testi e le categorie del mock-up (`mockup.js`: `WA_TEMPLATE_NAMES`, `WA_TEMPLATE_TEXTS`,
 * `WA_TEMPLATE_META`). Una azienda che non ne ha li riceve alla prima lettura (D5); poi sono suoi e li cambia.
 */
export const DEFAULT_WHATSAPP_TEMPLATES = Object.freeze([
    template({
        key: "first_contact",
        name: "Primo contatto",
        category: "marketing",
        body: "Ciao {nome}, sono {commerciale} di {NostraAzienda}. Ti ho cercata al telefono per la tua richiesta su come far crescere {AziendaTarget}. Quando posso richiamarti?",
    }),
    template({
        key: "after_no_answer",
        name: "Dopo chiamata senza risposta",
        category: "marketing",
        body: "Ciao {nome}, ti ho appena chiamata ma eri impegnata, immagino in salone. Ti richiamo presto: se preferisci un orario, scrivimelo qui.",
    }),
    template({
        key: "after_answer",
        name: "Dopo la chiamata con risposta",
        category: "marketing",
        body: "Ciao {nome}, grazie per il tempo che mi hai dedicato al telefono. Se in futuro vorrai parlarne ancora per {AziendaTarget}, scrivimi pure qui. {commerciale} di {NostraAzienda}",
    }),
    template({
        key: "callback_confirmation",
        name: "Conferma richiamo",
        category: "utility",
        body: "Ciao {nome}, come d'accordo ti richiamo {giorno e ora}. A presto!",
    }),
    template({
        key: "disco_confirmation",
        name: "Conferma disco",
        category: "utility",
        body: "Ciao {nome}, ti confermo la nostra chiamata conoscitiva: {giorno e ora}. Ti chiamo io al numero che mi hai lasciato. A presto!",
    }),
    template({
        key: "demo_confirmation",
        name: "Conferma demo",
        category: "utility",
        body: "Ciao {nome}, ti confermo la demo su Google Meet: {giorno e ora}. Trovi il link nell'invito che ti ho mandato. A presto!",
    }),
]);
/**
 * Il modello che la postazione propone dopo l'esito di una chiamata (piano M6, R1, Task 4 e 5; questione 385: in M6 i passi dopo
 * la chiamata sono fissi come nel mock-up, `FLOWS`). La chiave è l'esito del primo passo (`no_answer`) o il secondo passo dopo
 * «Risposta» (`call_back`, `not_interested`, e da R7 `disco_booked` e `demo_booked` con la loro conferma). Gli altri esiti non
 * propongono nessun messaggio.
 */
export const WHATSAPP_SUGGESTED_AFTER = Object.freeze({
    no_answer: "after_no_answer",
    call_back: "callback_confirmation",
    not_interested: "after_answer",
    // Disco e demo prenotate (R7, TEL-20): la conferma, obbligatoria quando il Lead può ricevere il WhatsApp.
    disco_booked: "disco_confirmation",
    demo_booked: "demo_confirmation",
});
// Le cinque variabili e i nomi di prima, in una espressione sola: `renderTemplate`, `positionalParameters` e `canonicalTemplateBody`
// leggono il testo allo stesso modo. I nomi hanno soltanto lettere e spazi, nessun carattere speciale delle espressioni regolari, e
// si scrivono esatti (`{Nome}` o `{aziendatarget}` non sono variabili). Altre graffe (`{foo}`, il `{{1}}` di Meta) non sono variabili.
const VARIABLE_NAMES = [...TEMPLATE_VARIABLES, ...Object.keys(TEMPLATE_VARIABLE_ALIASES)].join("|");
const VARIABLE_PATTERN = new RegExp(`\\{(${VARIABLE_NAMES})\\}`, "g");
/** La variabile di un nome letto nel testo: il nome stesso, o quello nuovo di un nome di prima. */
function variableOf(name) {
    return TEMPLATE_VARIABLE_ALIASES[name] ?? name;
}
/**
 * Il testo con i nomi di adesso (WA-13): `{salone}` diventa `{AziendaTarget}`, `{azienda}` `{NostraAzienda}`; il resto, spazi compresi,
 * resta com'è. Così si mostra e si salva un modello: per Meta non cambia niente, perché il corpo posizionale, i parametri e i valori sono
 * gli stessi (`positionalTemplateBody`, `templateParametersOf`).
 */
export function canonicalTemplateBody(body) {
    return body.replace(VARIABLE_PATTERN, (_placeholder, name) => `{${variableOf(name)}}`);
}
/**
 * Riempie le variabili del testo con i valori dati; un nome di prima (`{salone}`) prende il valore di quello nuovo. Una variabile
 * senza valore (assente, vuota o di soli spazi) resta nel testo fra graffe, con il nome di adesso (`{nome}`, `{AziendaTarget}`), così
 * chi scrive la vede e la completa (D5: nel modo a mano la finestra chiede di completare il testo), non diventa mai «undefined», e si
 * elenca in `missing` una volta sola, nell'ordine della prima comparsa. Le variabili che il testo non usa non mancano a nessuno. Il
 * testo si legge una volta sola: un valore che contiene una variabile non si sostituisce ancora.
 */
export function renderTemplate(body, values) {
    const missing = [];
    const text = body.replace(VARIABLE_PATTERN, (_placeholder, name) => {
        const variable = variableOf(name);
        const value = values[variable];
        if (typeof value === "string" && value.trim() !== "")
            return value;
        if (!missing.includes(variable))
            missing.push(variable);
        return `{${variable}}`;
    });
    return { text, missing };
}
/**
 * Le variabili del testo nell'ordine della prima comparsa, senza doppioni: la posizione di ognuna è il suo `{{1}}`, `{{2}}`…
 * del modello di Meta (una variabile che compare due volte riusa lo stesso numero).
 */
export function positionalParameters(body) {
    const found = [];
    for (const match of body.matchAll(VARIABLE_PATTERN)) {
        const variable = variableOf(match[1] ?? "");
        if (!found.includes(variable))
            found.push(variable);
    }
    return found;
}
/**
 * I parametri di un modello per il WhatsApp ufficiale (piano M6, R2, Task 9a; ruling «Pulizia dei parametri»): il valore di ogni
 * variabile nell'ordine di `positionalParameters` (il `{{1}}`, `{{2}}`… del modello presso il fornitore), pulito con
 * `cleanTemplateParameter` come lo riceve il fornitore; `values` sono gli stessi valori puliti, con cui si scrive il testo che si
 * salva (così il messaggio salvato è quello che il Lead riceve); `missing` le variabili senza valore, nell'ordine della prima
 * comparsa: assenti, vuote o vuote dopo la pulizia (un valore di soli a capo o tabulazioni vale una variabile mancante e non parte
 * mai). Un modello con una variabile mancante non parte da un canale automatico (`whatsappRoute`, `missing_values`).
 */
export function templateParametersOf(body, values) {
    const parameters = [];
    const cleaned = {};
    const missing = [];
    for (const variable of positionalParameters(body)) {
        const value = cleanTemplateParameter(values[variable] ?? "");
        if (value === "") {
            missing.push(variable);
            continue;
        }
        parameters.push(value);
        cleaned[variable] = value;
    }
    return { parameters, values: cleaned, missing };
}
/**
 * Il testo di un modello come va all'approvazione del WhatsApp ufficiale (piano M6, R2, Task 8a): gli spazi e gli a capo ai bordi
 * si tolgono e ogni variabile diventa `{{n}}`, con `n` la sua posizione in `positionalParameters` (una variabile che compare due
 * volte riusa lo stesso numero). Le altre graffe restano come sono.
 */
export function positionalTemplateBody(body) {
    const text = body.trim();
    const order = positionalParameters(text);
    return text.replace(VARIABLE_PATTERN, (_placeholder, name) => `{{${order.indexOf(variableOf(name)) + 1}}}`);
}
// Una variabile, anche con il nome di prima, al primo o all'ultimo carattere del testo.
const VARIABLE_AT_START = new RegExp(`^\\{(${VARIABLE_NAMES})\\}`);
const VARIABLE_AT_END = new RegExp(`\\{(${VARIABLE_NAMES})\\}$`);
/**
 * Se il testo comincia o finisce con una variabile (questione 411): un modello così il WhatsApp ufficiale non lo approva, quindi
 * non si manda all'approvazione e la pagina lo dice prima («aggiungi una parola o un punto»). Gli spazi e gli a capo ai bordi non
 * contano; una parola o un punto prima della prima variabile, o dopo l'ultima, bastano.
 */
export function templateVariableAtEdge(body) {
    const text = body.trim();
    return VARIABLE_AT_START.test(text) || VARIABLE_AT_END.test(text);
}
// Un numero internazionale con le sole cifre (da 8 a 15, la prima non è zero), il «+» facoltativo: ogni numero che
// `normalizePhone` produce ci sta.
const INTERNATIONAL_NUMBER = /^\+?([1-9]\d{7,14})$/;
/** Il testo senza le metà di coppie surrogate: `encodeURIComponent` su una di esse lancia un errore, e il link non si aprirebbe. */
function wellFormed(text) {
    return Array.from(text, (char) => {
        const code = char.charCodeAt(0);
        return char.length === 1 && code >= 0xd800 && code <= 0xdfff ? "�" : char;
    }).join("");
}
/**
 * Il link del modo a mano (D4): `https://wa.me/<numero senza +>?text=<testo>`. Il testo si codifica con `encodeURIComponent`,
 * quindi accenti, emoji e a capo arrivano com'erano scritti e il testo non può uscire dalla richiesta. Un numero che non è
 * internazionale (`+393000000000`) è un errore di chi chiama: meglio nessun link di un link a un'altra chat.
 */
export function waMeLink(e164, text) {
    const digits = INTERNATIONAL_NUMBER.exec(e164)?.[1];
    if (digits === undefined)
        throw new RangeError("The WhatsApp number must be an international number, like +393000000000");
    return `https://wa.me/${digits}?text=${encodeURIComponent(wellFormed(text))}`;
}
/** La riga di partenza dell'azienda in fondo a ogni WhatsApp, anche a mano (D25, questione 395): l'azienda la può cambiare, non toglierla. */
export const DEFAULT_STOP_LINE = "Per non ricevere più messaggi scrivi STOP.";
/**
 * Mette la riga «scrivi STOP» in fondo al testo, dopo una riga vuota (D25): ogni messaggio la porta, quello dei modelli e quello
 * libero. Gli spazi e gli a capo in fondo al testo si tolgono prima, e gli spazi intorno alla riga non contano. Un testo che
 * finisce già con quella riga, intera, torna com'è (senza gli spazi in fondo): passarlo di nuovo non la scrive due volte. Una
 * riga vuota è un errore di chi chiama: meglio un errore di un messaggio senza la riga.
 */
export function withStopLine(text, stopLine) {
    const line = stopLine.trim();
    if (line === "")
        throw new RangeError("The stop line must not be empty");
    const body = text.trimEnd();
    return body === line || body.endsWith(`\n${line}`) ? body : `${body}\n\n${line}`;
}
// Le parole con cui un Lead chiede di non ricevere più messaggi (questione 393): il messaggio intero, il punto finale facoltativo.
const STOP_REQUEST = /^(?:stop|basta|cancellami)\.?$/i;
/**
 * Il messaggio di un Lead è una richiesta di STOP (questione 393)? Soltanto se è fatto di «STOP», «Basta» o «Cancellami»,
 * maiuscolo o minuscolo, con o senza il punto finale, e gli spazi intorno non contano. Un testo più lungo, anche se contiene la
 * parola («STOP per favore», «non voglio fermarmi»), non lo è: una frase non spegne i WhatsApp di nessuno.
 */
export function isStopRequest(text) {
    return STOP_REQUEST.test(text.trim());
}
/**
 * La scelta del canale per un messaggio (D2), l'unico posto in cui si decide: nessun ripiego silenzioso sparso nel codice.
 * Il primo motivo, in quest'ordine:
 * 1. Il Lead non si può contattare, o ha acceso «Non inviare WhatsApp»: blocco, su ogni canale (anche a mano) e prima di ogni ripiego.
 * 2. Nessun WhatsApp per la persona: ripiego a mano, `no_sender`.
 * 3. Il WhatsApp è `manual`: parte a mano. È la riserva e resta sempre accesa (questione 391), quindi non dipende dalla scelta
 *    dell'azienda né dallo stato del numero, e la finestra di 24 ore non la riguarda: scrive la persona, nella sua app.
 * 4. L'azienda ha spento il canale: ripiego a mano, `channel_disabled`; viene prima dello stato del numero.
 * 5. Il numero non è pronto: ripiego a mano, `sender_not_ready`.
 * 6. Testo libero: dentro le 24 ore parte, fuori è bloccato, `outside_service_window` (D11, su ogni canale automatico).
 * 7. Con il WhatsApp ufficiale il modello deve essere approvato da Meta, altrimenti ripiego a mano, `template_not_approved`
 *    (anche senza modello, se il testo non è libero). L'approvazione è una regola di Meta: Agile non passa dai suoi modelli
 *    (come nel mock-up, `waRoute`).
 * 8. Un modello con una variabile senza valore per questo Lead (anche vuota dopo la pulizia dei parametri) non parte da un canale
 *    automatico, dove il testo non si ritocca: ripiego a mano, `missing_values`, e la persona completa il testo (D5; piano M6, R2,
 *    Task 9a). Con Agile, dove il testo si ritocca (WA-6), `complete` dice il testo che parte.
 * 9. WhatsApp Agile al momento dell'invio (`agile`, R9), nell'ordine del ruling: il numero del Lead non ammesso
 *    (`recipient_not_allowed`), il tetto del giorno (`daily_cap`), il ritmo del numero (`paced`, con l'ora del prossimo), il canale
 *    che non consegna (`channel_down`), il fornitore che non risponde (`provider_unreachable`).
 * Il ripiego si dice sempre a chi scrive («Il WhatsApp ufficiale non è pronto: per ora a mano»).
 */
export function whatsappRoute(input) {
    if (input.leadBlocked !== null)
        return { kind: "blocked", reason: input.leadBlocked };
    const { sender } = input;
    if (sender === null)
        return { kind: "fallback_manual", reason: "no_sender" };
    if (sender.channel === "manual")
        return { kind: "send", channel: "manual" };
    if (!sender.enabledByCompany)
        return { kind: "fallback_manual", reason: "channel_disabled" };
    if (!sender.ready)
        return { kind: "fallback_manual", reason: "sender_not_ready" };
    if (input.freeText) {
        return input.withinServiceWindow ? { kind: "send", channel: sender.channel } : { kind: "blocked", reason: "outside_service_window" };
    }
    if (sender.channel === "meta" && input.template?.approval !== "approved")
        return { kind: "fallback_manual", reason: "template_not_approved" };
    if (input.template !== null && !input.template.complete)
        return { kind: "fallback_manual", reason: "missing_values" };
    if (sender.channel === "agile" && input.agile !== undefined) {
        const { agile } = input;
        if (!agile.recipientAllowed)
            return { kind: "fallback_manual", reason: "recipient_not_allowed" };
        if (agile.capReached)
            return { kind: "fallback_manual", reason: "daily_cap" };
        if (agile.pacedUntil !== null)
            return { kind: "fallback_manual", reason: "paced", nextAt: agile.pacedUntil };
        if (agile.channel === "down")
            return { kind: "fallback_manual", reason: "channel_down" };
        if (agile.channel === "unreachable")
            return { kind: "fallback_manual", reason: "provider_unreachable" };
    }
    return { kind: "send", channel: sender.channel };
}
/**
 * Il ritmo di WhatsApp Agile (R9, lezione di GioiaFlow): almeno 8 minuti fra due WhatsApp Agile dallo stesso numero, perché il
 * numero non sembri di uno spammer e non venga bloccato. Vale per ogni messaggio, anche per le conferme (che hanno il modo a mano).
 */
export const SENDER_MIN_GAP_MS = 8 * 60_000;
/**
 * Da quando può partire il prossimo WhatsApp Agile da un numero (R9): `lastSentAt` è l'ora dell'ultimo che il fornitore può avere
 * preso da quel numero (preso in carico, oppure non riuscito senza saperlo: la risposta persa o il «Non confermato»,
 * `WHATSAPP_MAYBE_TAKEN_FAILURES`; uno che il fornitore ha rifiutato non conta), `null` se non ce n'è. `null` se può partire adesso,
 * altrimenti l'ora da cui può partire: `SENDER_MIN_GAP_MS` dopo l'ultimo, che a quell'ora esatta parte.
 */
export function pacedUntil(lastSentAt, now) {
    if (lastSentAt === null)
        return null;
    const next = new Date(lastSentAt.getTime() + SENDER_MIN_GAP_MS);
    return now.getTime() < next.getTime() ? next : null;
}
/**
 * Per quanto l'ultimo stato «giù» salvato del canale di WhatsApp Agile tiene la finestra a mano (R9): 10 minuti dall'ultima risposta
 * del fornitore. Dopo, la finestra offre l'invio, e l'invio chiede di nuovo il canale al fornitore e ripiega a mano, detto ad alta
 * voce, se è ancora giù.
 */
export const STORED_CHANNEL_DOWN_HOLD_MS = 10 * 60_000;
/**
 * Se l'ultimo stato salvato del canale tiene il messaggio a mano, senza chiedere al fornitore (R9): soltanto un «giù» di meno di
 * `STORED_CHANNEL_DOWN_HOLD_MS`. Un canale che consegna, uno senza stato (mai controllato) o un «giù» più vecchio lasciano decidere
 * all'invio, che lo chiede al fornitore.
 */
export function storedChannelDownHolds(stored, now) {
    if (stored === null || stored.healthy)
        return false;
    return now.getTime() - stored.checkedAt.getTime() < STORED_CHANNEL_DOWN_HOLD_MS;
}
const SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;
/**
 * La finestra di servizio di Meta (D11): 24 ore esatte dall'ultimo messaggio del Lead, aperta finché `now - lastInboundAt` è
 * minore di 24 ore (a 23:59:59.999 dentro, a 24:00:00.000 fuori). Senza messaggi in arrivo è chiusa: una chiamata senza
 * risposta non la apre. Un messaggio «nel futuro» (orologi che non coincidono) vale come appena arrivato; una data che non
 * esiste la chiude.
 */
export function withinServiceWindow(lastInboundAt, now) {
    if (lastInboundAt === null)
        return false;
    return now.getTime() - lastInboundAt.getTime() < SERVICE_WINDOW_MS;
}
/** Quanti WhatsApp al giorno allo stesso Lead dai canali automatici (D12, questione 354). */
export const DAILY_RECIPIENT_CAP = 2;
/**
 * Perché si scrive con un modello (questione 413): i modelli di categoria `utility` sono le conferme (richiamo, disco, demo), quindi
 * `service`; gli altri `marketing`.
 */
export function templatePurpose(category) {
    return category === "utility" ? "service" : "marketing";
}
/**
 * Il tetto per destinatario (D12, WA-10): `sentToday` sono i WhatsApp già mandati oggi a quel Lead, anche quelli dichiarati a mano.
 * Le conferme (`service`) passano sempre ma contano; il marketing si ferma a `DAILY_RECIPIENT_CAP`. `allowed` è la regola di chi si
 * ferma al tetto (WhatsApp Agile, R9); `warn` dice soltanto che il tetto è raggiunto, per ogni scopo. L'avviso che la finestra e il
 * server mostrano con il WhatsApp ufficiale e a mano è `shouldWarnDailyCap`, che per le conferme non avvisa (questione 413).
 */
export function dailyRecipientCap(sentToday, purpose) {
    if (!Number.isInteger(sentToday) || sentToday < 0)
        throw new RangeError("sentToday must be a whole number of messages, zero or more");
    const reached = sentToday >= DAILY_RECIPIENT_CAP;
    return { allowed: purpose === "service" || !reached, warn: reached };
}
/**
 * L'avviso del tetto (questione 413), l'unica regola per la finestra del browser prima del clic e per la risposta del server
 * (preparazione a mano e «Invia il WhatsApp»): il WhatsApp ufficiale e il modo a mano non si fermano mai per il tetto, avvisano. Una
 * conferma (`service`) conta ma non avvisa mai; il resto avvisa da `DAILY_RECIPIENT_CAP` WhatsApp già mandati oggi a quel Lead. Un
 * conteggio che non è un numero intero di messaggi è un errore di chi chiama, come in `dailyRecipientCap`.
 */
export function shouldWarnDailyCap(sentToday, purpose) {
    const { warn } = dailyRecipientCap(sentToday, purpose);
    return purpose !== "service" && warn;
}
//# sourceMappingURL=whatsapp.js.map